import { useEffect, useMemo, useState } from "react";
import { Button, ButtonProps, Checkbox, Modal, Select, Tooltip } from "antd";
import { ExclamationCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

export type DeleteMilestoneMoveTarget = {
  id: string;
  name: string;
  committedDate: string | null;
};

export type DeleteMilestoneLinkedBid = {
  id: string;
  label: string;
};

export type DeleteMilestoneOptions = {
  moveToMilestoneId?: string;
};

export default function DeleteDesignPackageMilestoneModal(props: {
  open: boolean;
  milestoneName: string;
  designPackageName: string;
  linkedBids: DeleteMilestoneLinkedBid[];
  scopeCount: number;
  otherMilestones: DeleteMilestoneMoveTarget[];
  confirmLoading: boolean;
  onClose: () => void;
  onConfirm: (options: DeleteMilestoneOptions) => void;
}) {
  const {
    open,
    milestoneName,
    designPackageName,
    linkedBids,
    scopeCount,
    otherMilestones,
    confirmLoading,
    onClose,
    onConfirm
  } = props;
  const hasLinked = linkedBids.length > 0 || scopeCount > 0;
  const [keepLinked, setKeepLinked] = useState(true);
  const [moveToMilestoneId, setMoveToMilestoneId] = useState(
    otherMilestones[0]?.id ?? ""
  );

  useEffect(() => {
    if (!open) return;
    setKeepLinked(true);
    setMoveToMilestoneId(otherMilestones[0]?.id ?? "");
    // Reset keep/move only when the dialog opens, not when parent rows refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open
  }, [open]);

  const bidSummary = useMemo(() => {
    if (linkedBids.length === 0) return null;
    const names = linkedBids
      .map((b) => b.label)
      .filter(Boolean)
      .join(", ");
    return `${linkedBids.length} Bid Package${
      linkedBids.length === 1 ? "" : "s"
    }${names ? ` · ${names}` : ""}`;
  }, [linkedBids]);

  const packageLabel = designPackageName.trim() || "this design package";
  const canDelete = !hasLinked || !keepLinked || Boolean(moveToMilestoneId);

  // A milestone with nothing linked gets a confirm-style dialog. It is rendered
  // declaratively from `open` rather than opened with `modal.confirm()` in an
  // effect: StrictMode runs effects twice on mount, so the imperative version
  // opened, destroyed and reopened the dialog on every click (the flicker).
  if (!hasLinked) {
    return (
      <Modal
        open={open}
        onOk={() => onConfirm({})}
        onCancel={onClose}
        destroyOnHidden
        width={416}
        title={
          <span className="flex items-center gap-3">
            {/* Inline color: the global brand tint on .anticon would win over a class. */}
            <ExclamationCircleFilled
              style={{ color: "var(--ciq-warning-text)", fontSize: 22 }}
            />
            Delete {milestoneName}?
          </span>
        }
        okText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        closable={!confirmLoading}
        keyboard={!confirmLoading}
        mask={{ closable: !confirmLoading }}
        okButtonProps={
          {
            danger: true,
            className: "ciq-delete-btn",
            "data-testid": "delete-ms-confirm"
          } as ButtonProps
        }
        cancelButtonProps={
          {
            disabled: confirmLoading,
            "data-testid": "delete-ms-cancel"
          } as ButtonProps
        }
      >
        <span data-testid="delete-ms-modal">
          Proceeding will remove this milestone from the {packageLabel}.
        </span>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      centered
      width={480}
      title="Are you sure you want to delete Design Milestone?"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={confirmLoading}
            data-testid="delete-ms-cancel"
          >
            Cancel
          </Button>
          <Button
            danger
            type="primary"
            className="ciq-delete-btn"
            loading={confirmLoading}
            disabled={!canDelete}
            data-testid="delete-ms-confirm"
            onClick={() =>
              onConfirm(
                keepLinked && moveToMilestoneId ? { moveToMilestoneId } : {}
              )
            }
          >
            Delete milestone
          </Button>
        </div>
      }
    >
      <div data-testid="delete-ms-modal">
        <p className="m-0 text-sm leading-5 text-black/65">
          This deletes the {milestoneName} milestone from {packageLabel}, along
          with all linked items unless kept and reassigned.
        </p>
        <div className="mt-3 overflow-hidden rounded-lg border border-solid border-ciq-gray-100 bg-ciq-neutral-25 px-3 py-2 text-[13px] leading-5 text-black/88">
          {bidSummary ? <div>{bidSummary}</div> : null}
          {scopeCount > 0 ? (
            <div>
              {scopeCount} scope item{scopeCount === 1 ? "" : "s"} · assigned to
              this milestone
            </div>
          ) : null}
        </div>
        <label
          data-testid="delete-ms-keep"
          className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-black/88"
        >
          <Checkbox
            checked={keepLinked}
            disabled={confirmLoading}
            onChange={(e) => setKeepLinked(e.target.checked)}
          />
          Keep the linked Bid Packages and scope items
        </label>
        {keepLinked ? (
          <div className="mt-3">
            <div className="mb-1.5 flex items-center gap-1 text-[13px] text-black/88">
              Move linked Bid Package and scope items to
              <Tooltip title="Linked Bid Packages and scope items will be moved to the milestone you select.">
                <InfoCircleOutlined className="text-ciq-gray-400" />
              </Tooltip>
            </div>
            <Select
              data-testid="delete-ms-move-to"
              className="w-full"
              value={moveToMilestoneId || undefined}
              disabled={confirmLoading || otherMilestones.length === 0}
              placeholder="Select milestone"
              onChange={setMoveToMilestoneId}
              options={otherMilestones.map((m) => ({
                value: m.id,
                label: (
                  <span className="flex items-center justify-between gap-3">
                    <span>{m.name}</span>
                    {m.committedDate ? (
                      <span className="text-xs text-ciq-planner-ink4">
                        Committed{" "}
                        {DateUtils.format(
                          m.committedDate,
                          DATE_FORMAT_MMDDYYYY
                        )}
                      </span>
                    ) : null}
                  </span>
                )
              }))}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
